package fr.frux.feature.blockedusers

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.designsystem.components.UserModerationMessage
import fr.frux.shared.domain.model.BlockedUser
import fr.frux.shared.domain.repository.UserRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class BlockedUsersState(
    val users: List<BlockedUser> = emptyList(),
    val isLoading: Boolean = true,
    /** Catégorie d'échec du chargement (#176) ; le libellé est résolu par l'écran. */
    val error: AppError? = null,
    /** Comptes dont le réaffichage est en vol (garde anti double-tap par ligne). */
    val pendingUserIds: Set<Long> = emptySet(),
    /** Retour d'un réaffichage, à afficher en snackbar puis consommer. */
    val message: UserModerationMessage? = null,
)

/** « Comptes masqués » (#515, ADR 0029) : la liste de gestion pour revenir sur un masquage. */
class BlockedUsersViewModel(
    private val userRepository: UserRepository,
) : ViewModel() {
    private val _state = MutableStateFlow(BlockedUsersState())
    val state: StateFlow<BlockedUsersState> = _state.asStateFlow()

    init {
        load()
    }

    fun retry() = load()

    private fun load() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            userRepository.getBlockedUsers().fold(
                onSuccess = { users -> _state.update { it.copy(users = users, isLoading = false) } },
                onFailure = { error -> _state.update { it.copy(isLoading = false, error = error.asAppError()) } },
            )
        }
    }

    fun onUnblock(user: BlockedUser) {
        if (user.userId in _state.value.pendingUserIds) return
        _state.update { it.copy(pendingUserIds = it.pendingUserIds + user.userId, message = null) }
        viewModelScope.launch {
            userRepository.unblockUser(user.userId).fold(
                onSuccess = {
                    _state.update {
                        it.copy(
                            users = it.users.filterNot { row -> row.userId == user.userId },
                            pendingUserIds = it.pendingUserIds - user.userId,
                            message = UserModerationMessage.Unblocked(user.username),
                        )
                    }
                },
                onFailure = { error ->
                    _state.update {
                        it.copy(
                            pendingUserIds = it.pendingUserIds - user.userId,
                            message = UserModerationMessage.Failed(error.asAppError()),
                        )
                    }
                },
            )
        }
    }

    fun consumeMessage() {
        _state.update { it.copy(message = null) }
    }
}
